import { useState } from "react";
import { Loader2, Plus, Trash2, X, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { PortfolioPreview, RoomPreview } from "./GalleryPreview";

const FIELDS_PORTFOLIO = [
  "Real Estate", "Finance", "Government Policy", "Architecture",
  "Travel & Tourism", "Luxury Retail", "Marketing", "Fund Raising",
];

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function emptyItem(gallery) {
  return {
    gallery,
    section: "",
    image_data: "",
    image_position: "50% 50%",
    field: "",
    title: "",
    subtitle: "",
    description: "",
    location: "",
    event_date: "",
    story: "",
    outcome: [],
    quote: "",
    tag: "",
  };
}

export default function GalleryItemModal({ gallery, item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({ ...emptyItem(gallery), ...item });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateOutcome = (i, v) => {
    const next = [...(form.outcome || [])];
    next[i] = v;
    updateField("outcome", next);
  };
  const addOutcome = () => updateField("outcome", [...(form.outcome || []), ""]);
  const removeOutcome = (i) => {
    const next = [...(form.outcome || [])];
    next.splice(i, 1);
    updateField("outcome", next);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Image must be under 4MB.");
      return;
    }
    const dataUrl = await readAsDataURL(f);
    updateField("image_data", dataUrl);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.image_data) {
      setError("Image is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        outcome: (form.outcome || []).map((s) => s.trim()).filter(Boolean),
      };
      if (isNew) {
        await api.post("/gallery", payload);
        toast.success("Item added.");
      } else {
        const { id, gallery: _g, order, created_at, updated_at, ...patch } = payload;
        await api.put(`/gallery/${item.id}`, patch);
        toast.success("Item updated.");
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const Preview = gallery === "rooms" ? RoomPreview : PortfolioPreview;
  const isRoom = gallery === "rooms";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 bg-[#030712]/85 backdrop-blur-sm overflow-y-auto"
      onClick={() => !saving && onClose()}
      data-testid="gallery-item-modal"
    >
      <div
        className="glass-strong rounded-sm w-full max-w-6xl my-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-white/55 hover:text-white z-10"
          aria-label="Close"
          data-testid="gallery-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-3 p-8" data-testid="gallery-item-form">
            <p className="overline">{isRoom ? "In The Room" : "Selected Work"}</p>
            <h2 className="font-display text-2xl mt-1">{isNew ? "Add New Item" : "Edit Item"}</h2>

            <div className="mt-6 space-y-4">
              <label className="block">
                <span className="text-xs text-white/55 uppercase tracking-wider">Image</span>
                <div className="mt-2">
                  <input type="file" accept="image/*" onChange={onFile} className="text-sm text-white/70 file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-[var(--pov-bg-3)] file:text-white" data-testid="gallery-file" />
                </div>
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs text-white/55 uppercase tracking-wider">Section (optional)</span>
                  <input className="input mt-1" value={form.section} onChange={(e) => updateField("section", e.target.value)} placeholder="e.g. Public Policy" data-testid="gallery-section" />
                </label>
                <label className="block">
                  <span className="text-xs text-white/55 uppercase tracking-wider">Field / Category</span>
                  {isRoom ? (
                    <input className="input mt-1" value={form.field} onChange={(e) => updateField("field", e.target.value)} placeholder="e.g. Government Policy" data-testid="gallery-field" />
                  ) : (
                    <select className="input mt-1" value={form.field} onChange={(e) => updateField("field", e.target.value)} data-testid="gallery-field">
                      <option value="">— Select —</option>
                      {FIELDS_PORTFOLIO.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  )}
                </label>
              </div>

              <label className="block">
                <span className="text-xs text-white/55 uppercase tracking-wider">Title</span>
                <input className="input mt-1" required value={form.title} onChange={(e) => updateField("title", e.target.value)} data-testid="gallery-title" />
              </label>

              <label className="block">
                <span className="text-xs text-white/55 uppercase tracking-wider">Event Date (optional)</span>
                <input className="input mt-1" value={form.event_date || ""} onChange={(e) => updateField("event_date", e.target.value)} placeholder="e.g. April 2024 — free text, any format" data-testid="gallery-event-date" />
              </label>

              {isRoom ? (
                <label className="block">
                  <span className="text-xs text-white/55 uppercase tracking-wider">Location (optional)</span>
                  <input className="input mt-1" value={form.location} onChange={(e) => updateField("location", e.target.value)} placeholder="e.g. New York · EV Reform" data-testid="gallery-location" />
                </label>
              ) : null}

              {!isRoom ? (
                <label className="block">
                  <span className="text-xs text-white/55 uppercase tracking-wider">Description</span>
                  <textarea className="input mt-1 min-h-[100px]" value={form.description} onChange={(e) => updateField("description", e.target.value)} data-testid="gallery-description" />
                </label>
              ) : null}

              {isRoom ? (
                <>
                  <label className="block">
                    <span className="text-xs text-white/55 uppercase tracking-wider">Story</span>
                    <textarea className="input mt-1 min-h-[140px]" value={form.story} onChange={(e) => updateField("story", e.target.value)} data-testid="gallery-story" />
                  </label>

                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/55 uppercase tracking-wider">Outcome bullets</span>
                      <button type="button" onClick={addOutcome} className="text-xs text-[var(--pov-coral)] hover:text-[var(--pov-coral-2)] inline-flex items-center gap-1" data-testid="add-outcome">
                        <Plus className="w-3 h-3" /> Add bullet
                      </button>
                    </div>
                    <div className="mt-2 space-y-2">
                      {(form.outcome || []).length === 0 ? (
                        <p className="text-xs text-white/40 italic">No outcomes yet. Add one to give the card weight.</p>
                      ) : null}
                      {(form.outcome || []).map((o, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`outcome-row-${i}`}>
                          <input className="input flex-1" value={o} onChange={(e) => updateOutcome(i, e.target.value)} placeholder="e.g. Bill amendment passed" />
                          <button type="button" onClick={() => removeOutcome(i)} className="w-9 h-9 grid place-items-center rounded-sm border border-white/10 hover:border-[var(--pov-coral)] text-white/60 hover:text-[var(--pov-coral)]" data-testid={`remove-outcome-${i}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <label className="block">
                    <span className="text-xs text-white/55 uppercase tracking-wider">Bottom tag (optional)</span>
                    <input className="input mt-1" value={form.tag} onChange={(e) => updateField("tag", e.target.value)} placeholder="e.g. Public-sector engagement" data-testid="gallery-tag" />
                  </label>
                </>
              ) : null}

              {error ? <p className="text-sm text-[var(--pov-coral)]" data-testid="gallery-form-error">{error}</p> : null}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="btn-secondary text-sm" disabled={saving} data-testid="gallery-cancel">
                  Cancel
                </button>
                <button className="btn-primary text-sm" disabled={saving} data-testid="gallery-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {isNew ? "Add Item" : "Save Changes"}
                </button>
              </div>
            </div>
          </form>

          {/* Live Preview */}
          <aside className="lg:col-span-2 p-8 bg-[#020610]/60 border-l border-white/5">
            <div className="flex items-center justify-between mb-4">
              <p className="overline">Live Preview</p>
              {form.image_data ? (
                <button
                  type="button"
                  onClick={() => updateField("image_position", "50% 50%")}
                  className="text-[10px] text-white/55 hover:text-white tracking-wider uppercase"
                  data-testid="reset-image-position"
                >
                  Center image
                </button>
              ) : null}
            </div>
            <Preview item={form} onPositionChange={(p) => updateField("image_position", p)} />
            <p className="mt-6 text-xs text-white/40">
              Exactly how this card will appear on the live site. Drag the photo to position the
              focal point.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}
